import { z } from 'zod';
import { amountInputSchema, amountSchema, nonNegativeAmountInputSchema } from '../common/amount.js';
import { paginationQuerySchema } from '../common/pagination.js';
import { isoDateTimeSchema } from '../common/primitives.js';
import { transferDirectionSchema } from '../transfers/direction.js';
import { branchRefSchema, dateOnlySchema, employeeRefSchema, recordStatusSchema, } from '../common/refs.js';
/**
 * Boxes, bank accounts and balances (transfers.md sections 2 and 6).
 *
 * A balance = opening balance + every recorded movement and never goes below
 * zero. The opening balance is entered once at creation and never edited;
 * corrections use إضافة رصيد / سحب رصيد. A holder used by any movement other
 * than its opening balance cannot be deleted, only disabled.
 */
export const HOLDER_KINDS = ['BOX', 'BANK_ACCOUNT'];
export const holderKindSchema = z.enum(HOLDER_KINDS);
export const holderRefSchema = z.object({
    kind: holderKindSchema,
    id: z.string(),
    /** Box name, or "{bankName} — {accountName}" for an account. */
    name: z.string(),
});
export const holderListQuerySchema = paginationQuerySchema.extend({
    status: recordStatusSchema.optional(),
    branchId: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Boxes — الصناديق
// ---------------------------------------------------------------------------
export const createBoxSchema = z.object({
    branchId: z.string().min(1),
    name: z.string().trim().min(1).max(80),
    /** Unique across boxes. */
    code: z.string().trim().min(1).max(20),
    openingBalance: nonNegativeAmountInputSchema,
});
/** The opening balance is create-only; the branch of a used box cannot change. */
export const updateBoxSchema = createBoxSchema.omit({ openingBalance: true }).partial();
export const boxViewSchema = z.object({
    id: z.string(),
    name: z.string(),
    code: z.string(),
    branch: branchRefSchema,
    openingBalance: amountSchema,
    balance: amountSchema,
    status: recordStatusSchema,
    used: z.boolean(),
    createdAt: isoDateTimeSchema,
    createdBy: employeeRefSchema,
    updatedAt: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// Bank accounts — الحسابات البنكية
// ---------------------------------------------------------------------------
export const ibanSchema = z
    .string()
    .trim()
    .toUpperCase()
    .transform((v) => v.replace(/\s+/g, ''))
    .pipe(z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/, 'must be a valid IBAN'));
export const accountNumberSchema = z.string().trim().min(1).max(40);
const bankAccountFields = z.object({
    bankName: z.string().trim().min(1).max(120),
    accountName: z.string().trim().min(1).max(120),
    accountNumber: accountNumberSchema.nullable().optional(),
    iban: ibanSchema.nullable().optional(),
    branchIds: z.array(z.string().min(1)).min(1),
});
/** At least one of رقم الحساب or IBAN; each unique. */
export const createBankAccountSchema = bankAccountFields
    .extend({ openingBalance: nonNegativeAmountInputSchema })
    .refine((v) => Boolean(v.accountNumber) || Boolean(v.iban), {
    message: 'enter the account number or the IBAN',
    path: ['accountNumber'],
});
export const updateBankAccountSchema = bankAccountFields.partial();
export const bankAccountViewSchema = z.object({
    id: z.string(),
    bankName: z.string(),
    accountName: z.string(),
    accountNumber: z.string().nullable(),
    iban: z.string().nullable(),
    branches: z.array(branchRefSchema),
    openingBalance: amountSchema,
    balance: amountSchema,
    status: recordStatusSchema,
    used: z.boolean(),
    createdAt: isoDateTimeSchema,
    createdBy: employeeRefSchema,
    updatedAt: isoDateTimeSchema,
});
/**
 * GET /transfer-options — what the حوالة جديدة form offers in the session
 * branch: active boxes of that branch with balances, and active accounts
 * allowed in it (TRF-16).
 */
export const transferOptionsSchema = z.object({
    branch: branchRefSchema,
    boxes: z.array(z.object({ id: z.string(), name: z.string(), code: z.string(), balance: amountSchema })),
    bankAccounts: z.array(z.object({
        id: z.string(),
        bankName: z.string(),
        accountName: z.string(),
        accountNumber: z.string().nullable(),
        iban: z.string().nullable(),
        balance: amountSchema,
    })),
});
// ---------------------------------------------------------------------------
// Balance movements (section 6.3)
// ---------------------------------------------------------------------------
/**
 * POST /balance-adjustments — إضافة رصيد (IN) or سحب رصيد (OUT) on one holder.
 * Date: today by default, never in the future. A withdrawal below zero is
 * refused with INSUFFICIENT_BALANCE (TRF-17). Idempotent.
 */
export const balanceAdjustmentSchema = z.object({
    holder: z.object({ kind: holderKindSchema, id: z.string().min(1) }),
    direction: z.enum(['IN', 'OUT']),
    amount: amountInputSchema,
    reason: z.string().trim().min(1).max(500),
    /** Omitted = today in the organisation time zone, decided by the server. */
    effectiveDate: dateOnlySchema.optional(),
});
/**
 * POST /balance-moves — نقل رصيد between a box and a bank account, either
 * direction; one movement with both sides (TRF-18). Idempotent.
 */
export const balanceMoveSchema = z
    .object({
    from: z.object({ kind: holderKindSchema, id: z.string().min(1) }),
    to: z.object({ kind: holderKindSchema, id: z.string().min(1) }),
    amount: amountInputSchema,
    reason: z.string().trim().min(1).max(500),
})
    .refine((v) => v.from.kind !== v.to.kind, {
    message: 'a move is between a box and a bank account',
    path: ['to'],
});
export const balanceMoveViewSchema = z.object({
    id: z.string(),
    number: z.number().int().positive(),
    from: holderRefSchema,
    to: holderRefSchema,
    amount: amountSchema,
    reason: z.string(),
    fromBalanceAfter: amountSchema,
    toBalanceAfter: amountSchema,
    createdBy: employeeRefSchema,
    createdAt: isoDateTimeSchema,
});
/** Movement types of كشف الحركات. */
export const LEDGER_ENTRY_TYPES = [
    'OPENING',
    'TRANSFER',
    'TRANSFER_CANCEL',
    'TRANSFER_EDIT',
    'ADJUST_IN',
    'ADJUST_OUT',
    'MOVE',
];
export const ledgerEntryTypeSchema = z.enum(LEDGER_ENTRY_TYPES);
export const ledgerEntryViewSchema = z.object({
    id: z.string(),
    type: ledgerEntryTypeSchema,
    /** Signed: positive = in, negative = out. */
    amount: amountSchema,
    balanceAfter: amountSchema,
    effectiveDate: dateOnlySchema,
    createdAt: isoDateTimeSchema,
    createdBy: employeeRefSchema,
    reason: z.string().nullable(),
    /** Transfer number, or the move number, shown as the reference. */
    reference: z.string().nullable(),
    transferId: z.string().nullable(),
    /**
     * The customer of the transfer behind a TRANSFER-family entry (T2, TRF-55):
     * the party who was present at the branch (`presentPartyRole`).
     */
    customerName: z.string().nullable(),
    customerNotPresent: z.boolean().default(false),
    /** The direction of the transfer behind a TRANSFER-family entry (TRF-46). */
    transferDirection: transferDirectionSchema.nullable(),
    moveId: z.string().nullable(),
    /** For a move: the other side. */
    counterpart: holderRefSchema.nullable(),
});
/**
 * GET /boxes/:id/ledger and /bank-accounts/:id/ledger (كشف الحركات): a
 * standard paginated list, newest first by default; the holder and its
 * current balance come from GET /boxes/:id or /bank-accounts/:id.
 */
export const ledgerQuerySchema = paginationQuerySchema.extend({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    type: ledgerEntryTypeSchema.optional(),
});
export const transferFilterOptionsSchema = z.object({
    branches: z.array(branchRefSchema),
    employees: z.array(employeeRefSchema),
    boxes: z.array(z.object({ id: z.string(), name: z.string() })),
    bankAccounts: z.array(z.object({ id: z.string(), bankName: z.string(), accountName: z.string() })),
});
//# sourceMappingURL=contract.js.map